import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import HelpOutlineOutlined from "@mui/icons-material/HelpOutlineOutlined";
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { type ManifestDocumentAuditQuery } from "generated-graphql/graphql";
import { Link as RouterLink } from "react-router-dom";
import { prettyPrintDateTimeMed } from "util/dates";

import { SectionCard } from "./SectionCard";
import { SectionHeader } from "./SectionHeader";

interface DocumentAuditSectionProps {
  audit: ManifestDocumentAuditQuery["manifestDocumentAudit"] | undefined;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  tenantId: string;
  isDocumentStale: boolean;
}

function AuditValue({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography component="dt" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Typography component="dd" variant="body1" sx={{ m: 0, overflowWrap: "anywhere" }}>
        {value}
      </Typography>
    </Box>
  );
}

export function DocumentAuditSection({
  audit,
  loading,
  error,
  onRetry,
  tenantId,
  isDocumentStale,
}: DocumentAuditSectionProps) {
  const initialLoading = loading && audit === undefined;

  return (
    <Box component="section" aria-label="Document audit">
      <SectionCard>
        <SectionHeader title="Document audit">
          <Tooltip title="Visible only to Encamp staff." placement="top">
            <IconButton size="small" color="staff" aria-label="Staff-only section">
              <HelpOutlineOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </SectionHeader>
        {initialLoading && (
          <Stack spacing={1} aria-label="Loading document audit">
            <Skeleton width="60%" />
            <Skeleton height={100} />
          </Stack>
        )}
        {error && (
          <Alert severity="error" action={<Button onClick={onRetry}>Retry</Button>}>
            Could not load document audit.
          </Alert>
        )}
        {!initialLoading && !error && audit === null && (
          <Typography variant="body2" color="text.secondary">
            No source document available.
          </Typography>
        )}
        {audit && (
          <>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1 }}>
              <DescriptionOutlined color="action" sx={{ fontSize: 32 }} />
              <Box sx={{ minWidth: 0 }}>
                <Link
                  component={RouterLink}
                  to={`/o/${tenantId}/documents/${audit.documentId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  color="text.primary"
                  sx={{ overflowWrap: "anywhere" }}
                >
                  {audit.documentTitle}
                </Link>
                <Typography variant="body2" color="text.secondary">
                  Current source document
                </Typography>
              </Box>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Box
              component="dl"
              sx={{
                m: 0,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                gap: 3,
              }}
            >
              <AuditValue label="Uploaded by" value={audit.uploadedBy} />
              <AuditValue
                label="Reviewed by"
                value={audit.needsReview ? "Not reviewed" : (audit.reviewedBy ?? "Unknown user")}
              />
              <AuditValue label="Uploaded at" value={prettyPrintDateTimeMed(audit.uploadedAt)} />
              <AuditValue
                label="Reviewed at"
                value={
                  !audit.needsReview && audit.reviewedAt
                    ? prettyPrintDateTimeMed(audit.reviewedAt)
                    : "Not reviewed"
                }
              />
            </Box>
            {isDocumentStale && !audit.needsReview && (
              <Alert severity="info" sx={{ mt: 2 }}>
                A newer document version is available. The extracted data and review shown here are
                from the previous version.
              </Alert>
            )}
          </>
        )}
      </SectionCard>
    </Box>
  );
}
